import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Users, Heart, MapPin, Calendar, TrendingUp, Globe } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const DashboardPage = () => {
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/stats`, { withCredentials: true });
      setStats(data);
    } catch (e) {
      console.error('Error fetching stats:', e);
    } finally {
      setLoading(false);
    }
  };

  const mainCards = [
    { label: 'Personnes',  value: stats?.persons,  icon: Users,    path: '/persons' },
    { label: 'Familles',   value: stats?.familys,  icon: Heart,    path: '/families' },
    { label: 'Lieux',      value: stats?.places,   icon: MapPin,   path: '/places' },
    { label: 'Événements', value: stats?.events,   icon: Calendar, path: '/events' },
  ];

  // Encodage Gramps : 0=Féminin, 1=Masculin, 2=Inconnu, 3=Autre
  const genderRows = [
    { label: 'Hommes',  key: 'males',   colorVar: 'var(--forest)' },
    { label: 'Femmes',  key: 'females', colorVar: 'var(--terracotta)' },
    { label: 'Inconnu', key: 'unknown', colorVar: 'var(--text-muted)' },
    { label: 'Autre',   key: 'other',   colorVar: 'var(--border-strong)' },
  ];

  return (
    <div data-testid="dashboard-page" className="space-y-10">
      <div>
        <h1 className="text-4xl font-bold mb-1"
          style={{ color: 'var(--forest)', fontFamily: 'Cormorant Garamond, serif' }}>
          Tableau de bord
        </h1>
        <p style={{ color: 'var(--text-muted)' }}>Vue d'ensemble de votre base généalogique</p>
      </div>

      {loading ? (
        <div className="text-center py-12" style={{ color: 'var(--text-muted)' }}>Chargement…</div>
      ) : (
        <>
          {/* ── Compteurs principaux ── */}
          <section>
            <h2 className="text-xs font-semibold uppercase tracking-widest mb-4"
              style={{ color: 'var(--text-muted)' }}>Totaux</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {mainCards.map((card, idx) => {
                const Icon = card.icon;
                return (
                  <div key={idx}
                    data-testid={`stat-card-${card.label.toLowerCase()}`}
                    className="border p-6 transition-transform hover:-translate-y-1 cursor-pointer"
                    style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border)' }}
                    onClick={() => navigate(card.path)}
                  >
                    <Icon size={26} className="mb-3"
                      style={{ color: idx % 2 === 0 ? 'var(--forest)' : 'var(--terracotta)' }} />
                    <div className="text-3xl font-bold mb-1"
                      style={{ color: 'var(--text-primary)', fontFamily: 'Cormorant Garamond, serif' }}>
                      {card.value ?? '—'}
                    </div>
                    <div className="text-xs uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                      {card.label}
                    </div>
                    <div className="text-xs mt-2 font-medium" style={{ color: 'var(--forest)' }}>
                      Voir →
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* ── Stats supplémentaires ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Top noms de famille */}
            {stats?.top_surnames?.length > 0 && (
              <section className="border p-6"
                style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border)' }}>
                <div className="flex items-center gap-2 mb-5">
                  <TrendingUp size={18} style={{ color: 'var(--forest)' }} />
                  <h2 className="text-lg font-semibold"
                    style={{ color: 'var(--forest)', fontFamily: 'Cormorant Garamond, serif' }}>
                    Noms de famille les plus portés
                  </h2>
                </div>
                <ol className="space-y-2">
                  {stats.top_surnames.map((s, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <span className="w-6 text-right text-xs shrink-0"
                        style={{ color: 'var(--text-muted)' }}>{i + 1}.</span>
                      <div
                        className="flex-1 rounded-sm overflow-hidden h-7 relative cursor-pointer"
                        style={{ backgroundColor: 'var(--bg-subtle)' }}
                        onClick={() => navigate(`/persons?search=${encodeURIComponent(s.surname)}`)}
                        title={`Voir les personnes portant le nom « ${s.surname} »`}
                      >
                        <div className="h-full absolute left-0 top-0 rounded-sm transition-opacity hover:opacity-30"
                          style={{
                            width: `${(s.count / stats.top_surnames[0].count) * 100}%`,
                            backgroundColor: 'var(--forest)',
                            opacity: 0.15,
                          }} />
                        <span className="absolute inset-0 flex items-center px-3 text-sm font-medium hover:underline"
                          style={{ color: 'var(--text-primary)' }}>
                          {s.surname}
                        </span>
                      </div>
                      <span className="text-sm font-semibold w-8 text-right shrink-0"
                        style={{ color: 'var(--forest)' }}>{s.count}</span>
                    </li>
                  ))}
                </ol>
              </section>
            )}

            {/* Genre + moyenne enfants */}
            <section className="border p-6 space-y-6"
              style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border)' }}>
              {stats?.gender_stats && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Users size={18} style={{ color: 'var(--terracotta)' }} />
                    <h2 className="text-lg font-semibold"
                      style={{ color: 'var(--forest)', fontFamily: 'Cormorant Garamond, serif' }}>
                      Répartition par genre
                    </h2>
                  </div>
                  <div className="space-y-3">
                    {genderRows.map(({ label, key, colorVar }) => {
                      const val   = Number(stats.gender_stats[key] ?? 0);
                      const total = Number(stats.persons) || 1;
                      const pct   = Math.round((val / total) * 100);
                      if (val === 0) return null;
                      return (
                        <div key={key}>
                          <div className="flex justify-between text-sm mb-1">
                            <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
                            <span style={{ color: 'var(--text-primary)' }}>
                              {val} <span style={{ color: 'var(--text-muted)' }}>({pct}%)</span>
                            </span>
                          </div>
                          <div className="h-2 rounded-full overflow-hidden"
                            style={{ backgroundColor: 'var(--bg-subtle)' }}>
                            <div className="h-full rounded-full transition-all"
                              style={{ width: `${pct}%`, backgroundColor: colorVar }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {stats?.avg_children_per_family != null && (
                <div className="pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
                  <div className="flex items-center gap-2 mb-1">
                    <Heart size={16} style={{ color: 'var(--terracotta)' }} />
                    <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                      Moyenne d'enfants par famille
                    </span>
                  </div>
                  <p className="text-3xl font-bold pl-6"
                    style={{ color: 'var(--forest)', fontFamily: 'Cormorant Garamond, serif' }}>
                    {Number(stats.avg_children_per_family).toFixed(1)}
                  </p>
                </div>
              )}
            </section>

            {/* Top lieux */}
            {stats?.top_places?.length > 0 && (
              <section className="border p-6 lg:col-span-2"
                style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border)' }}>
                <div className="flex items-center gap-2 mb-5">
                  <Globe size={18} style={{ color: 'var(--forest)' }} />
                  <h2 className="text-lg font-semibold"
                    style={{ color: 'var(--forest)', fontFamily: 'Cormorant Garamond, serif' }}>
                    Lieux les plus fréquents
                  </h2>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  {stats.top_places.map((p, i) => (
                    <div key={i}
                      className="border p-3 text-center cursor-pointer transition-transform hover:-translate-y-0.5"
                      style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-subtle)' }}
                      onClick={() => navigate('/places')}
                    >
                      <MapPin size={16} className="mx-auto mb-1" style={{ color: 'var(--terracotta)' }} />
                      <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                        {p.title || 'Inconnu'}
                      </p>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                        {p.count} événement{p.count > 1 ? 's' : ''}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        </>
      )}
    </div>
  );
};